import { Anchor, Badge, Center, Code, Drawer, Group, Loader, Stack, Text, Tooltip } from '@mantine/core'
import { Link } from 'react-router-dom'
import { EmptyState, MetaList } from '@lighthouse/design'
import { IconAlertCircle, IconQuestionMark } from '@tabler/icons-react'
import { RestrictedState } from '../access/RestrictedState'
import { useBackstopRequest } from '../../hooks/useRequestBackstop'
import { isApiError, problemDetailText } from '../../services/apiError'
import { NOT_MEASURED, errorKind, errorSummary, whenLabel } from '../../utils/format'
import type { BackstopRequestDetail } from '../../types/requestBackstop'
import { InterventionPanel } from './InterventionPanel'
import {
  kindLabel, operationLabel, reasonHint, reasonLabel, requestAuditLink, routedToText, stateLabel, subjectLine,
} from './requestBackstopFormat'

const SURFACE = 'The request backstop'

/**
 * One request in full, opened by `/requests/:requestId` — the route a Cartographer notice's `observatory:/requests/{id}`
 * link lands on (doc 15 Spec B). It answers for a request that has since left the backstop, and says so, so an old
 * notice never reads as a broken link.
 */
export function BackstopDrawer({ requestId, enabled, onClose }: {
  requestId: string | null
  /** The page's gate (adminPageMayFetch): never fetched on `denied` or `pending`. */
  enabled: boolean
  onClose: () => void
}) {
  return (
    <Drawer
      opened={requestId !== null}
      onClose={onClose}
      position="right"
      size="xl"
      title={<Text fw={600}>Request</Text>}
    >
      {/* Keyed on the id, so moving to another request remounts the body rather than flashing the last one. */}
      {requestId && <BackstopDrawerBody key={requestId} id={requestId} enabled={enabled} />}
    </Drawer>
  )
}

/**
 * The drawer's contents. Exported for the render tests: Mantine's Drawer portals its children, and a portal renders
 * nothing under react-dom/server.
 */
export function BackstopDrawerBody({ id, enabled = true }: { id: string; enabled?: boolean }) {
  const { data, isLoading, isError, error } = useBackstopRequest(id, { enabled })

  if (isError) {
    const kind = errorKind(error)
    if (kind === 'not-found') {
      return (
        <div data-backstop-detail="missing">
          <EmptyState
            icon={<IconQuestionMark size={28} stroke={1.5} />}
            title="No such request"
            message="Nothing in Cartographer's request table has this id."
          />
        </div>
      )
    }
    if (kind === 'forbidden') {
      return <RestrictedState surface={SURFACE} reason={isApiError(error) ? problemDetailText(error.body) : null} />
    }
    const summary = errorSummary(error)
    return (
      <div data-backstop-detail="error">
        <EmptyState
          icon={<IconAlertCircle size={28} stroke={1.5} />}
          title="Could not read this request"
          message="The request failed, so nothing is known here about it."
          action={summary ? <Code style={{ overflowWrap: 'anywhere' }}>{summary}</Code> : undefined}
        />
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <Center py="xl" data-backstop-detail="loading">
        <Loader size="sm" />
      </Center>
    )
  }

  return <BackstopRequestView detail={data} />
}

function orDash(value: string | null | undefined) {
  return value?.trim() ? value : NOT_MEASURED
}

function BackstopRequestView({ detail }: { detail: BackstopRequestDetail }) {
  const request = detail.request
  const resolved = request.resolvedAt != null

  return (
    <Stack gap="md" data-backstop-detail="ready">
      <Stack gap={4}>
        <Group gap="xs">
          <Badge size="sm" variant="light" color="gray" style={{ textTransform: 'none' }}>{kindLabel(request.kind)}</Badge>
          <Badge size="sm" variant="outline" color={resolved ? 'gray' : 'blue'} style={{ textTransform: 'none' }}>
            {stateLabel(request.state)}
          </Badge>
        </Group>
        <Text fw={600} style={{ overflowWrap: 'anywhere' }}>{subjectLine(request)}</Text>
        {request.onBackstop ? (
          <Group gap={6} data-backstop-reason={request.backstopReason ?? ''}>
            <Tooltip label={reasonHint(request.backstopReason) ?? ''} multiline w={300} disabled={!reasonHint(request.backstopReason)}>
              <Badge size="sm" color="orange" variant="light" style={{ textTransform: 'none' }}>
                {reasonLabel(request.backstopReason)}
              </Badge>
            </Tooltip>
            <Text size="xs" c="dimmed">on the backstop since {whenLabel(request.onBackstopSince)}</Text>
          </Group>
        ) : (
          <Text size="xs" c="dimmed" data-backstop-reason="">
            {resolved
              ? 'This request has ended, so it is not on the backstop and nothing can be done to it.'
              : 'This request is not on the backstop: it is with whoever it was routed to.'}
          </Text>
        )}
      </Stack>

      <MetaList
        items={[
          { label: 'Asked by', value: orDash(request.requesterDisplayName) },
          { label: 'Raised', value: whenLabel(request.createdAt) },
          { label: 'Why', value: orDash(request.requestNote) },
          { label: 'Routed to', value: routedToText(request) },
          ...(request.kind === 'Commission' ? [{ label: 'Assigned to', value: orDash(request.assigneeDisplayName) }] : []),
          { label: 'Due', value: request.dueAt ? whenLabel(request.dueAt) : NOT_MEASURED },
          {
            label: 'First told',
            value: request.firstNotifiedAt ? whenLabel(request.firstNotifiedAt) : `${NOT_MEASURED} nobody has been told`,
          },
          ...(request.retractionRequestedAt
            ? [{ label: 'Retraction asked for', value: whenLabel(request.retractionRequestedAt) }]
            : []),
          ...(resolved
            ? [
              { label: 'Ended', value: whenLabel(request.resolvedAt) },
              { label: 'Ended by', value: orDash(request.resolvedByDisplayName) },
              { label: 'Resolution', value: orDash(request.resolutionNote) },
            ]
            : []),
        ]}
      />

      <InterventionPanel detail={detail} />

      <Stack gap={6}>
        <Text size="sm" fw={600}>History</Text>
        {detail.history.length === 0 ? (
          <Text size="sm" c="dimmed">No step of this request is recorded in the audit trail.</Text>
        ) : (
          <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }} data-backstop-history="">
            {detail.history.map((entry) => (
              <li key={entry.id}>
                <Text size="sm">{operationLabel(entry.operation)}{entry.userName ? ` by ${entry.userName}` : ''}</Text>
                <Text size="xs" c="dimmed">{whenLabel(entry.timestamp)}</Text>
              </li>
            ))}
          </ol>
        )}
        <Anchor component={Link} to={requestAuditLink(request.id)} size="xs" data-backstop-audit-link="">
          This request in the audit trail →
        </Anchor>
      </Stack>
    </Stack>
  )
}
